import { useEffect, useRef, useState } from 'react';

function AssetSelector({ 
    topic, 
    platform, 
    visualMode, 
    imagePrompt, 
    isGeneratingImage, 
    customImage, 
    customAudio, 
    onTopicChange, 
    onPlatformChange, 
    onVisualModeChange, 
    onImagePromptChange, 
    onGenerateImage, 
    onImageUpload, 
    onAudioUpload 
}) {
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);

    const togglePlay = (e) => {
        e.stopPropagation();
        if (playing) {
            audioRef.current.pause();
            setPlaying(false);
        } else {
            audioRef.current.play();
            setPlaying(true);
        }
    };

    useEffect(() => {
        const currentAudio = audioRef.current;
        return () => {
            if (currentAudio) currentAudio.pause();
        };
    }, []);

    return (
        <div className="sa-card">
            {customAudio && <audio ref={audioRef} src={customAudio} onEnded={() => setPlaying(false)} />}

            <div className="sa-step-label">
                <div className="sa-step-num">1</div>
                <span className="sa-step-name">Configure Your Post</span>
            </div>

            {/* --- Platform Selector --- */}
            <div className="sa-card__title">1. Choose Platform</div>
            <div className="sa-platform-selector">
                <button 
                    className={`sa-platform-btn ${platform === 'instagram' ? 'active' : ''}`}
                    onClick={() => onPlatformChange('instagram')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                    Instagram
                </button>
                <button 
                    className={`sa-platform-btn ${platform === 'linkedin' ? 'active' : ''}`}
                    onClick={() => onPlatformChange('linkedin')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                    LinkedIn
                </button>
            </div>

            {/* --- Topic --- */}
            <div className="sa-card__title">2. Desired Topic</div>
            <div style={{ marginBottom: 24 }}>
                <input 
                    type="text" 
                    className="sa-settings-panel__input" 
                    style={{ width: '100%', padding: '12px 16px', fontSize: '1rem' }}
                    placeholder="E.g. Digital Sovereignty, Strategic Focus, Inner Peace..."
                    value={topic}
                    onChange={(e) => onTopicChange(e.target.value)}
                />
            </div>

            {/* --- Visual Workflow Selector --- */}
            <div className="sa-card__title">3. Visual Creation Mode</div>
            <div className="sa-workflow-selector">
                <button 
                    className={`sa-workflow-btn ${visualMode === 'upload' ? 'active' : ''}`}
                    onClick={() => onVisualModeChange('upload')}
                >
                    <div className="sa-workflow-btn__icon">📁</div>
                    <div className="sa-workflow-btn__text">
                        <strong>Custom Upload</strong>
                        <span>Use your own background & music</span>
                    </div>
                </button>
                <button 
                    className={`sa-workflow-btn ${visualMode === 'generate' ? 'active' : ''}`}
                    onClick={() => onVisualModeChange('generate')}
                >
                    <div className="sa-workflow-btn__icon">✨</div>
                    <div className="sa-workflow-btn__text">
                        <strong>AI Visual Creator</strong>
                        <span>Generate a unique post image</span>
                    </div>
                </button>
            </div>

            {/* --- Conditional Visual Inputs --- */}
            {visualMode === 'upload' ? (
                <>
                    <div className="sa-card__title" style={{ fontSize: '0.8rem', opacity: 0.7 }}>Background Image</div>
                    <div className="sa-upload-zone" style={{ marginBottom: 24 }}>
                        {customImage ? (
                            <div className="sa-upload-preview">
                                <img src={customImage} alt="Preview" className="sa-upload-preview__img" />
                                <label className="sa-btn sa-btn--outline sa-upload-label">
                                    Change Image
                                    <input type="file" accept="image/*" onChange={onImageUpload} style={{ display: 'none' }} />
                                </label>
                            </div>
                        ) : (
                            <label className="sa-upload-placeholder">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                <span>Upload Background Image</span>
                                <input type="file" accept="image/*" onChange={onImageUpload} style={{ display: 'none' }} />
                            </label>
                        )}
                    </div>

                    <div className="sa-card__title" style={{ fontSize: '0.8rem', opacity: 0.7 }}>Background Music</div>
                    <div className="sa-upload-zone">
                        {customAudio ? (
                            <div className="sa-upload-preview sa-upload-preview--audio">
                                <div className="sa-audio-item active" style={{ flex: 1, margin: 0 }}>
                                    <button className={`sa-audio-play-btn ${playing ? 'playing' : ''}`} onClick={togglePlay}>
                                        {playing ? '⏸' : '▶'}
                                    </button>
                                    <div className="sa-audio-item__name">Custom Audio File</div>
                                </div>
                                <label className="sa-btn sa-btn--outline sa-upload-label" style={{ padding: '8px 12px', fontSize: '0.8rem' }}>
                                    Change
                                    <input type="file" accept="audio/*" onChange={onAudioUpload} style={{ display: 'none' }} />
                                </label>
                            </div>
                        ) : (
                            <label className="sa-upload-placeholder">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                                <span>Upload Background Music</span>
                                <input type="file" accept="audio/*" onChange={onAudioUpload} style={{ display: 'none' }} />
                            </label>
                        )}
                    </div>
                </>
            ) : (
                <div className="sa-ai-generator animate-fade">
                    <div className="sa-card__title" style={{ fontSize: '0.8rem', opacity: 0.7 }}>Describe the Visual</div>
                    <textarea 
                        className="sa-quote-edit" 
                        style={{ height: '80px', marginBottom: 16 }}
                        placeholder="E.g. A futuristic city skyline with neon lights, cinematic lighting, ultra detailed..."
                        value={imagePrompt}
                        onChange={(e) => onImagePromptChange(e.target.value)}
                    />
                    
                    <button 
                        className="sa-generate-btn" 
                        onClick={onGenerateImage}
                        disabled={isGeneratingImage}
                    >
                        {isGeneratingImage ? (
                            <>
                                <div className="sa-spinner" />
                                Generating Your Visual...
                            </>
                        ) : (
                            <>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" /></svg>
                                Generate Post Image
                            </>
                        )}
                    </button>

                    {customImage && visualMode === 'generate' && (
                        <div className="sa-upload-preview" style={{ marginTop: 20 }}>
                            <img src={customImage} alt="Generated" className="sa-upload-preview__img" />
                            <div className="sa-badge">AI Generated</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default AssetSelector;
